import React, {
  useState, useEffect, useCallback, useRef,
} from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { graphql } from 'react-apollo';

import { withRouter } from 'react-router-dom';
import {
  Message, Button, Confirm, Dimmer, Loader,
} from 'semantic-ui-react';
import { isEmpty } from 'lodash';

import ACCESS from '../constants/access';
import APP_PATH from '../constants/paths';
import client from '../apollo';
import withPagination from './WithPagination';
import LoadingDataEnhancer from './LoadingData';
import NewFlatRateOffer from '../views/FlatRatesOffers/NewForm';
import NewFlatRateOfferForm from '../views/FlatRatesOffers/NewFlatRateOfferForm';
import EditOfferBatch from '../views/FlatRatesOffers/EditOfferBatch';
import FlatRateOfferBatchesList from '../views/FlatRatesOffers/FlatRateOfferBatchesList';
import createFlatRateOfferMutation from '../graphql/createFlatRateOfferMutation';
import flatRateOfferBatchesQuery from '../graphql/flatRateOfferBatchesQuery';
import flatRateOfferBatchQuery from '../graphql/flatRateOfferBatchQuery';
import resetFlatRateOfferBatchMutation from '../graphql/resetFlatRateOfferBatchMutation';
import confirmFlatRateOfferBatchMutation from '../graphql/confirmFlatRateOfferBatchMutation';
import deleteFlatRateOfferBatchMutation from '../graphql/deleteFlatRateOfferBatchMutation';
import updateFlatRateOfferBatchMutation from '../graphql/updateFlatRateOfferBatchMutation';
import addOfferInFlatRateOfferBatchMutation from '../graphql/addOfferInFlatRateOfferBatchMutation';
import acceptFlatRateOfferBatchMutation from '../graphql/acceptFlatRateOfferBatchMutation';
import rejectFlatRateOfferBatchMutation from '../graphql/rejectFlatRateOfferBatchMutation';

import { allowAccess } from '../utilities/user';
import Search from '../views/Shared/Search';
import { setSearch } from '../data/searchField/actions';
import { setPage } from '../data/page/actions';
import { selectFlatRateOffersProductFamily } from '../data/flatRateOffers/selectors';
import GuaranteePanel from '../components/GuaranteePanel';
import ProductSelector from '../components/ProductSelector';
import { GUARANTEE_PRODUCT_LIST, PRODUCT_FAMILY_PAGE_OFFSET_STYLE } from '../constants/productFamily';
import {
  useResetFlatRateOffersProductFamilyOnExit,
  useFlatRateOffersProductFamily,
  useFlatRateOffersProductFamilyTheme,
} from '../hooks/useFlatRateOffersProductFamily';

// A mutation can report failure with an `errors` array that is empty or holds only blank
// strings. Passing that array straight to the error banner rendered a red box with no text,
// leaving users with no idea what went wrong, so always resolve it to a readable message.
const toErrorMessage = errors => {
  const message = (errors || []).filter(entry => entry && String(entry).trim()).join(', ');
  return message || 'Something went wrong. Please try again or contact support.';
};

const LIST_LOAD_ERROR = 'Could not load DS 360 offers. Change the product or search again to retry.';

const isInvalidPriceInParams = flatRateOfferInput => {
  if (flatRateOfferInput && flatRateOfferInput.flatRateOffers) {
    return flatRateOfferInput.flatRateOffers.some(offer => (
      !offer.price
        || offer.price < 0
        || (offer.daysPsi && (!offer.psiBundlePrice || offer.psiBundlePrice < 0))
    ));
  }
  return true;
};

const FlatRateOffers = props => {
  const {
    data: { flatRateOfferBatches, refetch, variables },
    searchFields,
    productFamily,
    user,
    createMutation,
    updateMutation,
    rejectMutation,
    acceptMutation,
    addOfferMutation,
    deleteMutation,
    resetMutation,
    confrimMutation,
    setSearchFields: setSearchFieldsAction,
    setPage: setPageAction,
    history,
    match,
  } = props;

  const [flashMessage, setFlashMessage] = useState(null);
  const [isNewModalOpen, setIsNewModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [selectedFlatRateOfferBatch, setSelectedFlatRateOfferBatch] = useState(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isNewFlatRateOfferFormOpen, setIsNewFlatRateOfferFormOpen] = useState(false);
  const [resetForm, setResetForm] = useState(false);
  const [isProductFamilyLoading, setIsProductFamilyLoading] = useState(false);
  const [listLoadFailed, setListLoadFailed] = useState(false);
  const requestedProductFamily = useRef(variables.productFamily);
  const productFamilyRequestId = useRef(0);

  useEffect(() => {
    if (productFamily === requestedProductFamily.current) return;
    requestedProductFamily.current = productFamily;
    productFamilyRequestId.current += 1;
    const requestId = productFamilyRequestId.current;
    const isLatest = () => requestId === productFamilyRequestId.current;
    setIsProductFamilyLoading(true);
    setListLoadFailed(false);
    refetch({ productFamily })
      .then(() => {
        if (!isLatest()) return;
        setListLoadFailed(false);
        setPageAction({});
      })
      .catch(() => {
        if (isLatest()) setListLoadFailed(true);
      })
      .finally(() => {
        if (isLatest()) setIsProductFamilyLoading(false);
      });
  }, [productFamily, refetch, setPageAction]);

  useEffect(() => {
    const batchId = match.params.id;
    if (!isEmpty(batchId)) {
      client
        .query({
          query: flatRateOfferBatchQuery,
          variables: {
            id: parseInt(batchId, 10),
          },
          fetchPolicy: 'network-only',
        })
        .then(({ data }) => {
          const { flatRateOfferBatch } = data;
          setIsEditModalOpen(true);
          setResetForm(true);
          setSelectedFlatRateOfferBatch(flatRateOfferBatch);
        });
    }
  }, [match.params.id]);

  const openNewForm = useCallback(() => {
    setIsNewModalOpen(true);
  }, []);

  const handleDismiss = useCallback(() => {
    setFlashMessage(null);
  }, []);

  const handleDeleteCancel = useCallback(() => {
    setIsDeleteConfirmationOpen(false);
  }, []);

  const onNewFormCloseHandler = useCallback(() => {
    setFormError(null);
    setSubmitting(null);
    setIsNewModalOpen(false);
  }, []);

  const onNewFlatRateOfferFormCloseHandler = useCallback(() => {
    setFormError(null);
    setSubmitting(null);
    setIsNewFlatRateOfferFormOpen(false);
  }, []);

  const onEditFormCloseHandler = useCallback(() => {
    setFormError(null);
    setSubmitting(null);
    setIsEditModalOpen(false);
  }, []);

  const resetFlatRateOfferBatch = useCallback(flatRateOfferBatch => {
    const { id } = flatRateOfferBatch;
    const input = { id };
    resetMutation({
      variables: { input },
    })
      .then(({ data }) => {
        const {
          resetFlatRateOfferBatch: { success, errors },
        } = data;
        if (success) {
          refetch();
          setSelectedFlatRateOfferBatch(null);
          setFlashMessage('Successfully Reset 360 Offer');
        } else {
          setSubmitting(false);
          setFlashMessage(errors);
        }
      })
      .catch(() => {
        setFlashMessage('Error while deleting 360 Offer');
      });
  }, [resetMutation, refetch]);

  const confirmFlatRateOfferBatch = useCallback(flatRateOfferBatch => {
    const { id } = flatRateOfferBatch;
    const input = { id };

    confrimMutation({
      variables: { input },
    })
      .then(({ data }) => {
        const {
          confirmFlatRateOfferBatch: { success, errors },
        } = data;
        if (success) {
          refetch();
          setSelectedFlatRateOfferBatch(null);
          setFlashMessage('Successfully Confirmed 360 Offer');
        } else {
          setSubmitting(false);
          setFlashMessage(errors);
        }
      })
      .catch(() => {
        setFlashMessage('Error while deleting 360 Offer');
      });
  }, [confrimMutation, refetch]);

  const onSelectFlatRateOfferBatch = useCallback((action, flatRateOfferBatch) => {
    setSelectedFlatRateOfferBatch(flatRateOfferBatch);

    switch (action) {
      case 'Edit':
        setIsEditModalOpen(true);
        break;
      case 'Reset':
        resetFlatRateOfferBatch(flatRateOfferBatch);
        break;
      case 'Confirm':
        confirmFlatRateOfferBatch(flatRateOfferBatch);
        break;
      case 'Delete':
        setIsDeleteConfirmationOpen(true);
        break;
      case 'AddNewOffer':
        setIsNewFlatRateOfferFormOpen(true);
        break;
      default:
        setSelectedFlatRateOfferBatch(null);
    }
  }, [resetFlatRateOfferBatch, confirmFlatRateOfferBatch]);

  const onNewFormSubmitHandler = useCallback(values => {
    const flatRateOfferInput = { ...values };
    setSubmitting(true);
    if (isInvalidPriceInParams(flatRateOfferInput)) {
      setSubmitting(false);
      setFormError('must be greater than 0');
      return;
    }
    createMutation({
      variables: { flatRateOfferInput },
    })
      .then(({ data }) => {
        const {
          createFlatRateOfferBatch: { success, errors },
        } = data;
        if (success) {
          setSubmitting(false);
          setFlashMessage('Successfully created 360 Offer');
          refetch();
          onNewFormCloseHandler();
        } else {
          setSubmitting(false);
          setFormError(toErrorMessage(errors));
        }
      })
      .catch(error => {
        setSubmitting(false);
        const errorMessage = error.message || error.toString() || 'An unexpected error occurred';
        setFormError(errorMessage);
      });
  }, [createMutation, refetch, onNewFormCloseHandler]);

  const onEditFormSubmitHandler = useCallback(values => {
    const input = { ...values };
    setSubmitting(true);
    updateMutation({
      variables: { input },
    })
      .then(({ data }) => {
        const {
          updateFlatRateOfferBatch: { success, errors },
        } = data;
        if (success) {
          setSubmitting(false);
          setIsEditModalOpen(false);
          setSelectedFlatRateOfferBatch(null);
          setResetForm(false);
          setFlashMessage('Successfully updated 360 Offer');

          if (resetForm) {
            history.push(APP_PATH.DEALSHIELD360_OFFERS);
          } else {
            refetch();
          }
        } else {
          setSubmitting(false);
          setFormError(toErrorMessage(errors));
        }
      })
      .catch(error => {
        setSubmitting(false);
        const errorMessage = error.message || error.toString() || 'An unexpected error occurred';
        setFormError(errorMessage);
      });
  }, [updateMutation, history, resetForm, refetch]);

  const onRejectHandler = useCallback(values => {
    const input = { ...values };
    setSubmitting(true);

    rejectMutation({
      variables: { input },
    })
      .then(({ data }) => {
        const {
          rejectFlatRateOfferBatch: { success, errors },
        } = data;
        if (success) {
          setSubmitting(false);
          setIsEditModalOpen(false);
          setSelectedFlatRateOfferBatch(null);
          setFlashMessage('Successfully rejected 360 Offer');
          refetch();
        } else {
          setSubmitting(false);
          setFormError(toErrorMessage(errors));
        }
      })
      .catch(() => {
        setIsDeleteConfirmationOpen(false);
      });
  }, [rejectMutation, refetch]);

  const onAcceptHandler = useCallback(values => {
    const input = { ...values };
    setSubmitting(true);

    acceptMutation({
      variables: { input },
    })
      .then(({ data }) => {
        const {
          acceptFlatRateOfferBatch: { success, errors },
        } = data;
        if (success) {
          setSubmitting(false);
          setIsEditModalOpen(false);
          setSelectedFlatRateOfferBatch(null);
          setFlashMessage('Successfully accepted 360 Offer');
          refetch();
        } else {
          setSubmitting(false);
          setFormError(toErrorMessage(errors));
        }
      })
      .catch(() => {
        setIsDeleteConfirmationOpen(false);
      });
  }, [acceptMutation, refetch]);

  const onNewFlatRateOfferFormSubmitHandler = useCallback(values => {
    const input = { ...values };
    setSubmitting(true);

    addOfferMutation({
      variables: { input },
    })
      .then(({ data }) => {
        const {
          addOfferInFlatRateOfferBatch: { success, errors },
        } = data;
        if (success) {
          setSubmitting(false);
          setIsNewFlatRateOfferFormOpen(false);
          setSelectedFlatRateOfferBatch(null);
          setFlashMessage('Successfully added 360 Offer');
          refetch();
        } else {
          setSubmitting(false);
          setFormError(toErrorMessage(errors));
        }
      })
      .catch(() => {
        setIsDeleteConfirmationOpen(false);
      });
  }, [addOfferMutation, refetch]);

  const onSearchSubmitHandler = useCallback(event => {
    const { auctionAccessNumber, dealerNumber } = event.target;
    const dealerNo = isEmpty(dealerNumber.value) ? null : dealerNumber.value;
    const variableArguments = {
      auctionAccessNumber: auctionAccessNumber.value || null,
      dealerNumber: dealerNo,
    };

    refetch({
      ...variableArguments,
    })
      .then(() => setListLoadFailed(false))
      .catch(() => setListLoadFailed(true));

    setSearchFieldsAction(variableArguments);
    setPageAction({});
  }, [refetch, setSearchFieldsAction, setPageAction]);

  const deleteFlatRateOfferBatch = useCallback(() => {
    const { id } = selectedFlatRateOfferBatch;
    const input = { id };

    deleteMutation({
      variables: { input },
    })
      .then(() => {
        refetch();
        setSelectedFlatRateOfferBatch(null);
        setIsDeleteConfirmationOpen(false);
        setFlashMessage('Successfully Deleted 360 Offer');
      })
      .catch(() => {
        setIsDeleteConfirmationOpen(false);
      });
  }, [deleteMutation, selectedFlatRateOfferBatch, refetch]);

  return (
    <div>
      {flashMessage && (
        <Message
          onDismiss={handleDismiss}
          content={flashMessage}
          positive
        />
      )}

      {isNewModalOpen && (
        <div>
          <NewFlatRateOffer
            isModalOpen={isNewModalOpen}
            onCloseHandler={onNewFormCloseHandler}
            onSubmitHandler={onNewFormSubmitHandler}
            error={formError}
            submitting={submitting}
          />
        </div>
      )}

      {isNewFlatRateOfferFormOpen && (
        <div>
          <NewFlatRateOfferForm
            isModalOpen={isNewFlatRateOfferFormOpen}
            onCloseHandler={onNewFlatRateOfferFormCloseHandler}
            onSubmitHandler={onNewFlatRateOfferFormSubmitHandler}
            error={formError}
            submitting={submitting}
            flatRateOfferBatch={selectedFlatRateOfferBatch}
          />
        </div>
      )}

      {isEditModalOpen && (
        <div>
          <EditOfferBatch
            isModalOpen={isEditModalOpen}
            onCloseHandler={onEditFormCloseHandler}
            onSubmitHandler={onEditFormSubmitHandler}
            error={formError}
            submitting={submitting}
            flatRateOfferBatch={selectedFlatRateOfferBatch}
            resetForm={resetForm}
            onRejectHandler={onRejectHandler}
            onAcceptHandler={onAcceptHandler}
          />
        </div>
      )}
      {user && allowAccess(user, ACCESS.DEALSHIELD360_CREATE_OFFERS) && (
        <Button color='green' onClick={openNewForm} inverted>
          {' '}
          Extend DS Offer{' '}
        </Button>
      )}
      <br />
      <br />

      <Search
        onSubmit={onSearchSubmitHandler}
        searchFields={searchFields}
        searchOptions={['auctionAccessNumber', 'dealerNumber']}
      />

      <Dimmer.Dimmable dimmed={isProductFamilyLoading}>
        <Dimmer active={isProductFamilyLoading} inverted>
          <Loader data-testid='list-loading-overlay'>Loading...</Loader>
        </Dimmer>
        {listLoadFailed ? (
          <Message negative data-testid='list-load-error' content={LIST_LOAD_ERROR} />
        ) : (
          <FlatRateOfferBatchesList
            onSelectFlatRateOfferBatch={onSelectFlatRateOfferBatch}
            flatRateOfferBatches={flatRateOfferBatches}
          />
        )}
      </Dimmer.Dimmable>
      <Confirm
        open={isDeleteConfirmationOpen}
        onCancel={handleDeleteCancel}
        onConfirm={deleteFlatRateOfferBatch}
      />
    </div>
  );
};

FlatRateOffers.propTypes = {
  data: PropTypes.shape({
    flatRateOfferBatches: PropTypes.array,
    refetch: PropTypes.func,
    variables: PropTypes.shape({
      productFamily: PropTypes.string,
    }).isRequired,
  }).isRequired,
  productFamily: PropTypes.string.isRequired,
  searchFields: PropTypes.shape({
    auctionAccessNumber: PropTypes.string,
    dealerNumber: PropTypes.string,
  }).isRequired,
  user: PropTypes.shape({
    roles: PropTypes.array,
  }),
  createMutation: PropTypes.func.isRequired,
  updateMutation: PropTypes.func.isRequired,
  rejectMutation: PropTypes.func.isRequired,
  acceptMutation: PropTypes.func.isRequired,
  addOfferMutation: PropTypes.func.isRequired,
  deleteMutation: PropTypes.func.isRequired,
  resetMutation: PropTypes.func.isRequired,
  confrimMutation: PropTypes.func.isRequired,
  setSearchFields: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
    }),
  }).isRequired,
};

FlatRateOffers.defaultProps = {
  user: null,
};

const mapDispatchToProps = dispatch => ({
  setSearchFields: fields => {
    dispatch(setSearch(fields));
  },
  setPage: () => {
    dispatch(setPage());
  },
});

const mapStateToProps = state => {
  const {
    data: { searchFields },
  } = state;
  return {
    searchFields,
    productFamily: selectFlatRateOffersProductFamily(state),
  };
};

const FlatRateOffersContainer = compose(
  withRouter,
  withPagination(flatRateOfferBatchesQuery, 'flatRateOfferBatches', {}, state => ({
    productFamily: selectFlatRateOffersProductFamily(state),
  })),
  graphql(createFlatRateOfferMutation, { name: 'createMutation' }),
  graphql(resetFlatRateOfferBatchMutation, { name: 'resetMutation' }),
  graphql(confirmFlatRateOfferBatchMutation, { name: 'confrimMutation' }),
  graphql(deleteFlatRateOfferBatchMutation, { name: 'deleteMutation' }),
  graphql(updateFlatRateOfferBatchMutation, { name: 'updateMutation' }),
  graphql(addOfferInFlatRateOfferBatchMutation, { name: 'addOfferMutation' }),
  graphql(acceptFlatRateOfferBatchMutation, { name: 'acceptMutation' }),
  graphql(rejectFlatRateOfferBatchMutation, { name: 'rejectMutation' }),
  LoadingDataEnhancer,
  connect(mapStateToProps, mapDispatchToProps)
)(FlatRateOffers);

const FlatRateOffersProductSelector = () => {
  const [selectedProduct, handleProductChange] = useFlatRateOffersProductFamily();
  const themeClass = useFlatRateOffersProductFamilyTheme();

  return (
    <ProductSelector
      products={GUARANTEE_PRODUCT_LIST}
      selectedProduct={selectedProduct}
      onProductChange={handleProductChange}
      tooltipContent='Select the product to filter DS 360 offers.'
      themeClass={themeClass}
    />
  );
};

const FlatRateOffersTheme = ({ children }) => {
  const themeClass = useFlatRateOffersProductFamilyTheme();
  return (
    <div data-testid='flat-rate-offers-theme' className={`theme ${themeClass}`} style={PRODUCT_FAMILY_PAGE_OFFSET_STYLE}>
      {children}
    </div>
  );
};

FlatRateOffersTheme.propTypes = {
  children: PropTypes.node.isRequired,
};

const FlatRateOffersPage = props => {
  useResetFlatRateOffersProductFamilyOnExit();
  return (
    <FlatRateOffersTheme>
      <GuaranteePanel>
        <FlatRateOffersProductSelector />
      </GuaranteePanel>
      {React.createElement(FlatRateOffersContainer, props)}
    </FlatRateOffersTheme>
  );
};

export default FlatRateOffersPage;
